import React, { useState } from 'react'
import { customerData } from '../../../utilities/CustomerData'
import { apiURL } from '../../../utilities/URLs'
import { refreshAccessToken } from '../../../utilities/extensions'
import { useNavigate } from 'react-router-dom'
import { ClipLoader } from 'react-spinners'
import { AlertTriangle, Download, ExternalLink } from 'lucide-react'
import { DateTime } from 'luxon'

export const DashboardDemandLetter = () => {
  const navigate = useNavigate()
  const [isDownloading, setIsDownloading] = useState(false)

  const handleDownload = async () => {
    setIsDownloading(true)
    try {
      const response = await fetch(`${apiURL}/api/getDemandLetterURL?pdfURL=${encodeURIComponent(customerData.customerDemandLetter.pdfUrl)}`, {
        headers: {
          'Authorization': customerData.accessToken
        }
      })
      const data = await response.json()
      if (data.response.error) {
        if (data.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          if (refreshedToken && refreshedToken.length > 0) {
            await handleDownload()
          } else {
            navigate('/', { state: { expired: true } })
          }
        } else {
          console.error(data.response.error)
        }
      } else {
        window.open(data.response.s3URL, '_blank')
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsDownloading(false)
    }
  }

  const formattedDate = customerData.customerDemandLetter.createdAt 
    ? DateTime.fromISO(customerData.customerDemandLetter.createdAt).toLocaleString(DateTime.DATE_MED)
    : ''

  return (
    <div className='relative overflow-hidden shadow-2xl p-8 rounded-3xl w-full h-fit flex flex-col md:flex-row items-center bg-gradient-to-br from-[#ef4444] via-[#b91c1c] to-[#991b1b] font-mont gap-8 mt-6 text-white border-4 border-white/20'>
      {/* Background patterns */}
      <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 bg-white/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-64 h-64 bg-black/10 rounded-full blur-3xl pointer-events-none" />
      
      <div className='flex-shrink-0 bg-white/20 p-4 rounded-2xl backdrop-blur-sm border border-white/30'>
        <AlertTriangle size={48} className="text-white animate-pulse" />
      </div>

      <div className='flex flex-col flex-grow gap-3'>
        <div className='flex flex-col md:flex-row md:items-end gap-2'>
          <h2 className='font-bold text-3xl tracking-tight'>Urgent Notice</h2>
          {formattedDate && (
            <span className='bg-black/20 px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-sm border border-white/10 uppercase tracking-widest mb-1'>
              Issued: {formattedDate}
            </span>
          )}
        </div>
        
        <p className='text-lg leading-relaxed font-medium opacity-95 max-w-3xl'>
          A formal demand letter has been issued regarding your account. Please review the document immediately to understand required actions and avoid further escalation.
        </p>

        <div className='flex flex-wrap items-center gap-6 mt-2'>
          <button 
            onClick={handleDownload} 
            disabled={isDownloading}
            className='group px-8 py-4 bg-white text-[#b91c1c] font-bold rounded-2xl shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-3 min-w-[240px]'
          >
            {isDownloading ? (
              <ClipLoader size={24} color="#b91c1c" />
            ) : (
              <>
                <Download size={20} className="group-hover:translate-y-0.5 transition-transform" />
                Download Demand Letter
                <ExternalLink size={16} className="opacity-50" />
              </>
            )}
          </button>
          
          <div className='flex flex-col'>
            <span className='text-sm font-semibold text-red-100 uppercase tracking-wider'>Status</span>
            <span className='text-white font-bold flex items-center gap-2'>
              <div className='w-2 h-2 bg-white rounded-full animate-ping' />
              Action Required
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
